import * as graph from '@adc/sdk'
import type { Listing, Maybe } from '@adc/sdk/types'
import { get, isString } from 'lodash'

// Type definitions
interface Address {
  state: string
  county: string
  city: string
  streetAddress: string
  postalCode: string
  countryCode: string
  formattedAddress: string | Maybe<string>[]
}

interface BreadCrumb {
  label: string
  url: string
}

interface VenueData {
  venueName: string
  venueUrl: string
  venueStreetAddress: string
  venueCity: string
  venueState: string
  venuePostalCode: string
  venueCountryCode: string
}

interface QAItem {
  question: string
  answer: string
}

interface ImageObject {
  '@type': 'ImageObject'
  contentUrl: string
}

interface VirtualLocation {
  '@type': 'VirtualLocation'
  url: string
}

interface PostalAddress {
  '@type': 'PostalAddress'
  streetAddress: string
  addressLocality: string
  addressRegion: string
  postalCode: string
  addressCountry: string
}

interface Place {
  '@type': 'Place'
  name: string
  address: PostalAddress
}

type EventLocation = VirtualLocation | Place

const EMPTY_VALUE = 'Unavailable'
const ADC_DEFAULT_VALUE = 'Auction.com'
const ADC_URL = 'https://www.auction.com'
const DEFAULT_EMPTY_ADDRESS: Address = {
  state: 'CA',
  county: 'Orange',
  city: 'Irvine',
  streetAddress: '1 Mauchly',
  postalCode: '92618',
  countryCode: 'US',
  formattedAddress: '1 Mauchly Irvine, CA 92618',
}
/* keyword list is based off of Google Search top queries */
const DEFAULT_ADC_KEYWORDS: string[] = [
  `${ADC_URL}`,
  `${ADC_DEFAULT_VALUE}`,
  'auction.com',
  'auction',
  'auction .com',
  'auctions near me',
  'house auctions near me',
  'auction houses near me',
  'auction .com',
  'auction homes',
  'house for auction near me',
  'foreclosures near me',
]

const DEFAULT_ADC_QA: QAItem[] = [
  {
    question: 'What is Auction.com',
    answer:
      "Auction.com is the nation's leading online marketplace focused on the sale of residential bank-owned and foreclosure properties via online auctions and in-person auction events. Auction.com goes beyond the bid to offer buyers a superior technology platform for convenient online and onsite access, an extensive inventory of over 13,000 properties, and supportive services and expertise dedicated to buyer success.",
  },
]

const breadCrumbList = (listing: Listing): BreadCrumb[] => {
  const { state, county, city, streetAddress } = listingAddress(listing)
  const listingPagePath = get(listing, 'listing_page_path', '')
  return [
    { label: 'auction.com', url: '/' },
    {
      label: `${state}`,
      url: `/residential/${state.toLowerCase()}`,
    },
    {
      label: `${county}`,
      url: `/residential/${state.toLowerCase()}/${county
        .replace(' ', '-')
        .toLowerCase()}-county`,
    },
    {
      label: `${city}`,
      url: `/residential/${state.toLowerCase()}/${city
        .replace(' ', '-')
        .toLowerCase()}_ct`,
    },
    { label: `${streetAddress}`, url: `${listingPagePath}` },
  ]
}

const eventSchemaAttendanceMode = (listing: Listing): string => {
  let eventAttendanceMode = ''
  if (
    isRemoteBidEnabled(listing) &&
    eventType(listing) === graph.enums.EventType.Live
  ) {
    eventAttendanceMode = 'MixedEventAttendanceMode'
  } else if (eventType(listing) === graph.enums.EventType.Live) {
    eventAttendanceMode = 'OfflineEventAttendanceMode'
  } else {
    eventAttendanceMode = 'OnlineEventAttendanceMode'
  }

  return eventAttendanceMode
}

const eventSchemaStatus = (listing: Listing): string => {
  if (listingStatusGroup(listing) === graph.enums.ListingStatusGroup.Canceled) {
    return 'EventCanceled'
  } else if (
    listingStatusGroup(listing) === graph.enums.ListingStatusGroup.Inactive
  ) {
    return 'EventPostponed'
  } else {
    return 'EventScheduled'
  }
}

const eventType = (listing: Listing): string =>
  get(listing, 'venue.event.event_type') || EMPTY_VALUE

const imageSrc = (
  listing: Listing,
  defaultImage: string
): string | ImageObject => {
  return (
    primaryPhoto(listing) || {
      '@type': 'ImageObject',
      contentUrl: defaultImage,
    }
  )
}

const isRemoteBidEnabled = (listing: Listing): boolean => {
  return get(listing, 'listing_summary.is_remote_bid_enabled', false)
}

const listingAddress = (listing: Listing): Address => {
  return {
    state:
      get(
        listing,
        'primary_property.summary.address.country_primary_subdivision'
      ) ||
      get(listing, 'seller_property.country_primary_subdivision') ||
      DEFAULT_EMPTY_ADDRESS.state,
    county:
      get(
        listing,
        'primary_property.summary.address.country_secondary_subdivision'
      ) ||
      get(listing, 'seller_property.country_secondary_subdivision') ||
      DEFAULT_EMPTY_ADDRESS.county,
    city:
      get(listing, 'primary_property.summary.address.municipality') ||
      get(listing, 'seller_property.municipality') ||
      DEFAULT_EMPTY_ADDRESS.city,
    streetAddress:
      get(listing, 'primary_property.summary.street_address') ||
      get(listing, 'seller_property.street_description') ||
      DEFAULT_EMPTY_ADDRESS.streetAddress,
    postalCode:
      get(listing, 'primary_property.summary.address.postal_code') ||
      get(listing, 'seller_property.postal_code') ||
      DEFAULT_EMPTY_ADDRESS.postalCode,
    countryCode:
      get(listing, 'primary_property.summary.address.country_code') ||
      DEFAULT_EMPTY_ADDRESS.countryCode,
    formattedAddress:
      get(listing, 'primary_property.summary.formatted_address') ||
      get(listing, 'formatted_address') ||
      DEFAULT_EMPTY_ADDRESS.formattedAddress,
  }
}

const listingVenue = (listing: Listing): VenueData => {
  return {
    venueName: get(listing, 'venue.venue_name', ''),
    venueUrl: get(listing, 'venue.venue_url', ''),
    venueStreetAddress: venueStreetAddress(listing),
    venueCity: get(listing, 'venue.venue_address.municipality', ''),
    venueState: get(
      listing,
      'venue.venue_address.country_primary_subdivision',
      ''
    ),
    venuePostalCode: get(listing, 'venue.venue_address.postal_code', ''),
    venueCountryCode: get(listing, 'venue.venue_address.country_code', ''),
  }
}

const listingStatusGroup = (listing: Listing): string | undefined =>
  get(listing, 'listing_status_group')

const primaryPhoto = (listing: Listing): string => {
  return get(listing, 'primary_photo', '') || ''
}

const listingPhotos = (listing: Listing, defaultPhoto: string): string[] => {
  const photos = get(listing, 'photos')
  return photos && photos.length
    ? photos.map((photo: any) => photo.file_descriptor.urls.cdn)
    : [defaultPhoto]
}

const venueStreetAddress = (listing: Listing): string => {
  const venueStreetUnit = get(listing, 'venue.venue_address.street_unit') || ''
  const venueStreetNumber =
    get(listing, 'venue.venue_address.street_number') || ''
  const venueStreetName =
    get(listing, 'venue.venue_address.street_name') ||
    DEFAULT_EMPTY_ADDRESS.streetAddress

  return `${venueStreetNumber} ${venueStreetName} ${venueStreetUnit}`.trim()
}

const eventLocation = (listing: Listing): EventLocation[] => {
  const locationSchema: EventLocation[] = []
  if (
    eventSchemaAttendanceMode(listing) === 'OnlineEventAttendanceMode' ||
    eventSchemaAttendanceMode(listing) === 'MixedEventAttendanceMode'
  ) {
    const virtualUrl = get(listing, 'venue.venue_url', '')
      ? get(listing, 'venue.venue_url')
      : get(listing, 'listing_page_path')
    locationSchema.push({
      '@type': 'VirtualLocation',
      url: get(listing, 'venue.venue_url', '')
        ? `${virtualUrl}`
        : `${ADC_URL}${virtualUrl}`,
    })
  }
  if (
    eventSchemaAttendanceMode(listing) === 'OfflineEventAttendanceMode' ||
    eventSchemaAttendanceMode(listing) === 'MixedEventAttendanceMode'
  ) {
    locationSchema.push({
      '@type': 'Place',
      name: get(listing, 'venue.venue_name') || EMPTY_VALUE,
      address: {
        '@type': 'PostalAddress',
        streetAddress: venueStreetAddress(listing),
        addressLocality:
          get(listing, 'venue.venue_address.municipality') ||
          DEFAULT_EMPTY_ADDRESS.city,
        addressRegion:
          get(listing, 'venue.venue_address.country_primary_subdivision') ||
          DEFAULT_EMPTY_ADDRESS.state,
        postalCode:
          get(listing, 'venue.venue_address.postal_code') ||
          DEFAULT_EMPTY_ADDRESS.postalCode,
        addressCountry:
          get(listing, 'venue.venue_address.country_code') ||
          DEFAULT_EMPTY_ADDRESS.countryCode,
      },
    })
  }

  return locationSchema
}

const propertyPrice = (listing: Listing): number => {
  if (get(listing, 'auction.starting_bid')) {
    return get(listing, 'auction.starting_bid') || 0
  } else {
    return get(listing, 'primary_property.summary.valuation') || 0
  }
}

const ADC_POSTAL_ADDRESS_SCHEMA = {
  '@type': 'PostalAddress' as const,
  addressCountry: DEFAULT_EMPTY_ADDRESS.countryCode,
  addressLocality: DEFAULT_EMPTY_ADDRESS.county,
  addressRegion: DEFAULT_EMPTY_ADDRESS.state,
  postalCode: DEFAULT_EMPTY_ADDRESS.postalCode,
  streetAddress: DEFAULT_EMPTY_ADDRESS.streetAddress,
}

const listingPageUrl = (listing: Listing): string => {
  const listingPagePath = get(listing, 'listing_page_path')
  return listingPagePath ? `${ADC_URL}${listingPagePath}` : ADC_URL
}

const getValidKeywords = (keywords: string[]): string[] => {
  return keywords.filter(word => isString(word))
}

export {
  breadCrumbList,
  eventLocation,
  eventSchemaAttendanceMode,
  eventSchemaStatus,
  eventType,
  imageSrc,
  isRemoteBidEnabled,
  listingAddress,
  listingPageUrl,
  listingPhotos,
  listingVenue,
  listingStatusGroup,
  primaryPhoto,
  propertyPrice,
  venueStreetAddress,
  EMPTY_VALUE,
  ADC_DEFAULT_VALUE,
  ADC_URL,
  ADC_POSTAL_ADDRESS_SCHEMA,
  getValidKeywords,
  DEFAULT_ADC_KEYWORDS,
  DEFAULT_ADC_QA,
}

// Type exports
export type {
  Address,
  BreadCrumb,
  VenueData,
  QAItem,
  ImageObject,
  VirtualLocation,
  Place,
  EventLocation,
}
